<script setup lang="ts">
  import { computed, onMounted, ref, useId, useSlots } from 'vue';
  import { FieldSize, FormField } from '../FormField';
  import {
    fieldClass,
    fieldSmallClass,
    iconClass,
    inputClass,
    inputSmallClass,
  } from './TextInput.css';
  import type { TextInputEmits, TextInputProps } from './TextInput.types';

  const props = withDefaults(defineProps<TextInputProps>(), {
    modelValue: null,
    type: 'text',
    disabled: false,
    required: false,
    autofocus: false,
    size: FieldSize.MD,
    announceWarning: true,
  });

  const emit = defineEmits<TextInputEmits>();

  const slots = useSlots();

  const autoId = useId();
  const inputId = computed(() => props.id ?? autoId);

  const inputRef = ref<HTMLInputElement | null>(null);

  const isSmall = computed<boolean>(() => props.size === FieldSize.SM);

  const onInput = (event: Event) => {
    emit('update:modelValue', (event.target as HTMLInputElement).value);
  };

  const onBlur = (event: FocusEvent) => {
    emit('blur', event);
  };

  onMounted(() => {
    if (props.autofocus) {
      inputRef.value?.focus();
    }
  });

  defineExpose({ inputRef });
</script>

<template>
  <FormField
    :id="inputId"
    :label="label"
    :helper-text="helperText"
    :error="error"
    :warning="warning"
    :announce-warning="announceWarning"
    :size="size"
    :disabled-reason="disabled ? disabledReason : undefined"
  >
    <template v-if="slots.extraLabel" #extraLabel>
      <slot name="extraLabel" />
    </template>

    <template #default="{ describedBy, hasError }">
      <div
        :class="[fieldClass, isSmall && fieldSmallClass]"
        :data-invalid="hasError ? '' : undefined"
        :data-disabled="disabled ? '' : undefined"
      >
        <span v-if="slots.leadingIcon" :class="iconClass">
          <slot name="leadingIcon" />
        </span>

        <input
          ref="inputRef"
          :id="inputId"
          :class="[inputClass, isSmall && inputSmallClass]"
          :data-test-id="testId"
          :type="type"
          :inputmode="inputMode"
          :pattern="pattern"
          :autocomplete="autocomplete"
          :aria-label="ariaLabel"
          :name="name"
          :value="modelValue ?? ''"
          :placeholder="placeholder"
          :disabled="disabled"
          :required="required"
          :autofocus="autofocus"
          :aria-invalid="hasError ? 'true' : undefined"
          :aria-describedby="describedBy"
          @input="onInput"
          @blur="onBlur"
        >

        <span v-if="slots.icon" :class="iconClass"> <slot name="icon" /> </span>
      </div>
    </template>
  </FormField>
</template>
